import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, DatePicker, Input, Row, Select, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';

import useAPIQuery from '../api/useAPIQuery';
import {
  SankhyaCompany,
  SankhyaDepartment,
  SankhyaJobRole,
  VixtingAffiliate,
  VixtingUnitBusiness,
} from '../types';
import Form from './Form';

// FIXME: Change names in the future
interface VixtingAdmissionFormProps {
  sankhyaCompanies: SankhyaCompany[];
  sankhyaJobRoles: SankhyaJobRole[];
  sankhyaDepartments: SankhyaDepartment[];
  affiliates?: VixtingAffiliate[];
  unitBusinesses?: VixtingUnitBusiness[];
}

const VixtingAdmissionForm = ({
  sankhyaCompanies,
  sankhyaJobRoles,
  sankhyaDepartments,
  affiliates = [],
  unitBusinesses = [],
}: VixtingAdmissionFormProps) => {
  const { t } = useTranslation();

  // TODO: VER SE TRADUZ AQ, se a vixting espera isso assim em portugues
  const vixting_admission_types = [
    'Aprendiz',
    'Autônomo(a)',
    'CLT',
    'Contrato por tempo indeterminado',
    'Empregado(a) Doméstico(a)',
    'Estagiário(a)',
    'Estatutário(a)',
    'Indeterminado',
    'Intermitente',
    'PJ',
    'Sócio(a) - Divisão de Lucros',
    'Sócio(a) - Pró Labore',
    'Temporário',
    'Voluntário(a)',
    'CLT Parcial',
  ];

  const config = useAPIQuery('/configs/tenant-configurations/');

  return (
    <>
      <Form.Item label={t('Regime de trabalho')} name="admissionType" rules={[{ required: true }]}>
        <Select
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
          }
          data-testid="select-vixting-job-role"
        >
          {vixting_admission_types.map((admissionKey) => (
            <Select.Option key={admissionKey}>{`${admissionKey}`}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label={
              <Typography.Text>
                <Trans>Data limite</Trans>{' '}
                <Tooltip
                  title={
                    <div style={{ textAlign: 'center' }}>
                      <Trans>
                        Data máxima para concluir o processo de admissão deste candidato no Vlow.
                      </Trans>
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Typography.Text>
            }
            name="vixtingLimitDate"
            shouldUpdate
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(current) => {
                // Dias anteriores ao dia de hoje não são permitidos
                return current && current < moment().startOf('day');
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('Celular')} name="candidatePhone">
            <NumberFormat format="(##) #####-####" customInput={Input} disabled placeholder="N/A" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="E-mail" name="candidateEmail" rules={[{ required: true }]}>
        <Input disabled />
      </Form.Item>
      <Form.Item label="CPF" name="candidateCPF" rules={[{ required: true }]}>
        <NumberFormat
          format="###.###.###-##"
          customInput={Input}
          data-testid="candidate-cpf-input"
          disabled
        />
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={t('Empresa')} name="sankhyaCompany" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-company"
            >
              {sankhyaCompanies.map(({ CODEMP, Empresa_NOMEFANTASIA }) => (
                <Select.Option key={CODEMP} value={`${CODEMP}`}>
                  <span title={CODEMP}>{`${Empresa_NOMEFANTASIA}`}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('Filial')} name="affiliate" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-vixting-affiliate"
            >
              {affiliates.map((affiliate) => (
                <Select.Option key={affiliate.id} value={`${affiliate.id}`}>
                  <span title={affiliate.id}>{`${affiliate.fantasyName}`}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {config.data?.enableVixtingUnitBusiness && (
        <Form.Item label={t('Unidade de Negócio')} name="unitBusiness">
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label as string).toLowerCase().includes(input.toLowerCase())
            }
            disabled
            data-testid="select-vixting-unitBusiness"
            options={unitBusinesses.map((unitBusiness) => ({
              value: `${unitBusiness.id}`,
              label: unitBusiness.name,
              key: unitBusiness.id,
            }))}
          />
        </Form.Item>
      )}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={t('Cargo')} name="sankhyaJobRole" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-job-role"
            >
              {sankhyaJobRoles.map(({ CODCARGO, DESCRCARGO }) => (
                <Select.Option key={CODCARGO} value={`${CODCARGO}`}>
                  {`${CODCARGO} - ${DESCRCARGO}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('Departamento')}
            name="sankhyaDepartment"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-department"
            >
              {sankhyaDepartments.map(({ CODDEP, DESCRDEP }) => (
                <Select.Option key={CODDEP} value={`${CODDEP}`}>
                  {`${CODDEP} - ${DESCRDEP}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default VixtingAdmissionForm;
